import { IBillGroupResp, IBillGroupRespUI } from '@monetize/types/app';
import {
  DEFAULT_PAGER,
  FilterType,
  TDataTablePager,
} from '@monetize/types/common';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetAccountContacts } from '../../../api/contactsService';
import { useGetListData } from '../../../api/queryUtils';
import {
  MBox,
  MFlex,
  MHStack,
  MPageHeader,
  MPageSearchInput,
} from '../../../components/Monetize';
import { DataTable } from '../../../components/Monetize/DataTableNew/DataTable';
import { DataTableRef } from '../../../components/Monetize/DataTableNew/DataTableTypes';
import { useTableColumns } from '../../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { ACTION_COLUMN_KEY } from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { ExportEntityButton } from '../../../components/Monetize/ExportEntityButton';
import { RefreshTableButton } from '../../../components/Monetize/RefreshTableButton';
import { getBillGroupPageRoute } from '../../../constants/routes';
import { useShowPastDueFields } from '../../../hooks';
import {
  buildFilterParamsRequestObject,
  getIsTrulyEmptyList,
  transformTableFilterValue,
} from '../../../utils';
import { addContactsAndPaymentsToBillGroupList } from '../../../utils/billGroups';
import { navigateOrOpenNewWindow } from '../../../utils/misc';
import { usePaymentMethodsData } from '../PaymentMethods/PaymentMethodList';
import { BillGroupActions } from './BillGroupActions';
import {
  BILL_GROUP_LIST_CATALOG,
  BillGroupListColumnContext,
} from './billGroupList.columns';

export const BillGroupListPage = ({
  billGroupId,
}: {
  billGroupId?: string;
}) => {
  const params = useParams();
  const navigate = useNavigate();
  const accountId = params?.accountId || '';
  const { listData: paymentMethods } = usePaymentMethodsData(accountId);
  const { data: contacts, isFetched } = useGetAccountContacts({
    accountId,
    config: { ...DEFAULT_PAGER, rows: 100 },
  });
  const [joinedData, setJoinedData] = useState<IBillGroupRespUI[]>([]);
  const tableRef = useRef<DataTableRef>(null);

  const [filters, setFilters] = useState<FilterType[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [pager, setPager] = useState<TDataTablePager>(DEFAULT_PAGER);

  const filterParams = useMemo(
    () => ({
      accountId,
      ...buildFilterParamsRequestObject(
        transformTableFilterValue(filters),
        searchTerm,
        'name',
      ),
    }),
    [accountId, filters, searchTerm],
  );

  const {
    data: billGroups,
    isLoading: loading,
    isError,
    error,
    refetch,
  } = useGetListData<IBillGroupResp>(
    'billGroups',
    { config: pager, filters: filterParams },
    {
      endpointArgs: { accountId },
      select: (data) => {
        if (billGroupId) {
          return {
            ...data,
            content: data.content?.filter(({ id }) => id === billGroupId),
          };
        }
        return data;
      },
    },
  );
  const showPastDue = useShowPastDueFields();

  useEffect(() => {
    setJoinedData(
      addContactsAndPaymentsToBillGroupList(
        billGroups?.content,
        paymentMethods?.content,
      ),
    );
  }, [billGroups, contacts, paymentMethods]);

  const isTrulyEmptyList = getIsTrulyEmptyList({
    loading,
    totalElements: billGroups?.totalElements || 0,
    filters,
    searchTerm,
    page: pager.page,
  });

  const onResetFilter = () => {
    setFilters([]);
    setPager(DEFAULT_PAGER);
    tableRef.current?.resetFilters();
  };

  const actionBodyTemplate = useCallback(
    (rowData: IBillGroupRespUI) => <BillGroupActions billGroup={rowData} />,
    [],
  );

  const columnContext = useMemo<BillGroupListColumnContext>(
    () => ({ actionBodyTemplate, showPastDue }),
    [actionBodyTemplate, showPastDue],
  );
  const { columns, state: columnState } = useTableColumns(
    BILL_GROUP_LIST_CATALOG,
    columnContext,
  );

  return (
    <MBox>
      <MPageHeader title="Bill Groups" hideContent={isTrulyEmptyList}>
        <MFlex>
          <MHStack gap="2">
            <MPageSearchInput
              value={searchTerm || ''}
              onChange={(e: any) => setSearchTerm && setSearchTerm(e)}
              count={billGroups?.totalElements}
              placeholderKey="Name"
              mode="alwaysExpanded"
            />
            <RefreshTableButton refetch={refetch} />
            <ExportEntityButton
              entity="billGroups"
              filters={filters}
              searchKey="name"
              searchTerm={searchTerm}
              sortField={pager.sortField}
              sortOrder={pager.sortOrder}
              endpointParams={[accountId]}
              getFilename={() => `bill-groups-${accountId}.csv`}
            />
          </MHStack>
        </MFlex>
      </MPageHeader>
      <DataTable
        ref={tableRef}
        columns={columns}
        state={columnState}
        data={joinedData}
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        totalElements={billGroups?.totalElements}
        totalPages={billGroups?.totalPages}
        pager={pager}
        setPager={setPager}
        initialSortingState={[{ id: 'createDate', desc: true }]}
        initialFilters={filters}
        onFiltersChange={(newFilters) => {
          setFilters(newFilters);
        }}
        onRowClick={(row, _tableRow, openInNewWindow) =>
          navigateOrOpenNewWindow(
            navigate,
            getBillGroupPageRoute(row.id!, 'details'),
            openInNewWindow,
          )
        }
        isLoading={loading || !isFetched}
        emptyProps={{
          mainMessage: 'Looks like there are no bill groups here.',
        }}
        emptyFilterResultsProps={{
          btnLabel: 'Reset Filters',
        }}
        resetFilter={onResetFilter}
      />
    </MBox>
  );
};
